import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * The design forbids a drawer on a drawer. A form that opens from inside the
 * big store-details drawer (record a payment, change the plan, edit the
 * details) therefore takes over that drawer's body — its own heading, a back
 * control, its submit in the drawer's bar — instead of sliding a second
 * drawer over it. Outside a host (the store-subscriptions list) the same
 * component is an ordinary drawer.
 *
 * The host keeps its own body mounted (hidden) while a panel is open, so the
 * component that owns the panel — and its state — survives until it closes.
 */
type PanelApi = {
  open: (id: string, onBack: () => void) => void;
  close: (id: string) => void;
};
type PanelState = {
  activeId: string | null;
  bar: HTMLElement | null;
  body: HTMLElement | null;
};

export const ApiContext = createContext<PanelApi | null>(null);
export const StateContext = createContext<PanelState>({ activeId: null, bar: null, body: null });

/** For the host drawer: wraps it, and tells it whether a panel holds its body. */
export function useDrawerPanels() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [bar, setBar] = useState<HTMLElement | null>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);
  const backs = useRef(new Map<string, () => void>());
  const order = useRef<string[]>([]);

  const open = useCallback((id: string, onBack: () => void) => {
    backs.current.set(id, onBack);
    order.current = [...order.current.filter((entry) => entry !== id), id];
    setActiveId(id);
  }, []);
  const close = useCallback((id: string) => {
    backs.current.delete(id);
    order.current = order.current.filter((entry) => entry !== id);
    setActiveId(order.current.at(-1) ?? null);
  }, []);
  const api = useMemo(() => ({ open, close }), [open, close]);
  const back = useCallback(() => {
    if (activeId) backs.current.get(activeId)?.();
  }, [activeId]);

  return {
    active: activeId !== null,
    back,
    /** The drawer bar's actions while a panel is open. */
    barSlot: <span ref={setBar} style={{ display: "contents" }} />,
    /** The drawer body while a panel is open. */
    bodySlot: <div ref={setBody} style={{ display: "flex", flexDirection: "column", gap: 16 }} />,
    wrap: (children: ReactNode) => (
      <ApiContext.Provider value={api}>
        <StateContext.Provider value={{ activeId, bar, body }}>{children}</StateContext.Provider>
      </ApiContext.Provider>
    ),
  };
}

